import { useEffect, useRef, useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { emptyPin, PinInput, pinArrayToString } from '@/components/user/PinInput';

interface PinVerificationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (pin: string) => void;
  loading?: boolean;
  title?: string;
  description?: string;
}

export function PinVerificationModal({
  open,
  onClose,
  onConfirm,
  loading = false,
  title = 'Enter transaction PIN',
  description = 'Confirm this purchase with your 4-digit PIN.',
}: PinVerificationModalProps) {
  const [pin, setPin] = useState(emptyPin());
  const [inputKey, setInputKey] = useState(0);
  const wasLoading = useRef(false);

  useEffect(() => {
    if (!open) {
      setPin(emptyPin());
      wasLoading.current = false;
    }
  }, [open]);

  // After a failed attempt (loading → idle while modal stays open), clear PIN for re-entry.
  useEffect(() => {
    if (wasLoading.current && !loading && open) {
      setPin(emptyPin());
      setInputKey((key) => key + 1);
    }
    wasLoading.current = loading;
  }, [loading, open]);

  const complete = pin.every(Boolean);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!complete || loading) return;
    onConfirm(pinArrayToString(pin));
  }

  return (
    <Modal open={open} onClose={onClose} title={title} description={description} size="xs">
      <form onSubmit={handleSubmit} className="space-y-4">
        <PinInput
          key={inputKey}
          value={pin}
          onChange={setPin}
          disabled={loading}
          autoFocus
          size="lg"
        />
        <div className="flex gap-2">
          <Button type="button" variant="outline" fullWidth onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" fullWidth loading={loading} loadingText="Processing..." disabled={!complete}>
            Confirm
          </Button>
        </div>
      </form>
    </Modal>
  );
}
